import { useInfiniteQuery } from '@tanstack/react-query';
import { Badge, Button, Spinner, Text, tokens } from '@fluentui/react-components';
import { ArrowClockwise24Regular } from '@fluentui/react-icons';
import { fetchWarehouseTransfers, type WarehouseTransferItem } from '../../api/inventory.api';
import { useT } from '../../i18n';
import { useIntlFormat } from '../../hooks/useIntlFormat';


/** Pestaña "Traspasos" de Inventario: lista de traspasos entre bodegas de la empresa. */
export function WarehouseTransfersTab() {
  const t = useT();
  const { fmtDateTime } = useIntlFormat();
  const formatDate = (iso: string) => (Number.isNaN(new Date(iso).getTime()) ? iso : fmtDateTime(iso));
  const query = useInfiniteQuery({
    queryKey: ['warehouse-transfers'],
    queryFn: ({ pageParam }) => fetchWarehouseTransfers(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items: WarehouseTransferItem[] = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <Button
          appearance="outline"
          icon={<ArrowClockwise24Regular />}
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          {t.inventory.transfersRefresh}
        </Button>
      </div>

      {query.isLoading ? (
        <Spinner size="small" label={t.inventory.transfersLoading} />
      ) : query.isError ? (
        <Text role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>
          {t.inventory.transfersLoadError}
        </Text>
      ) : items.length === 0 ? (
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.transfersEmpty}</Text>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{t.inventory.transfersColDate}</th>
                  <th style={{ textAlign: 'left' }}>{t.inventory.transfersColOrigin}</th>
                  <th style={{ textAlign: 'left' }}>{t.inventory.transfersColDestination}</th>
                  <th style={{ textAlign: 'left' }}>{t.inventory.transfersColProduct}</th>
                  <th style={{ textAlign: 'right' }}>{t.inventory.transfersColQty}</th>
                  <th style={{ textAlign: 'left' }}>{t.inventory.transfersColBy}</th>
                  <th style={{ textAlign: 'left' }} />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.transferId} data-testid="transfer-row">
                    <td>{formatDate(row.createdAt)}</td>
                    <td>{row.fromWarehouseName}</td>
                    <td>{row.toWarehouseName}</td>
                    <td>{row.productName}</td>
                    <td style={{ textAlign: 'right' }}>{row.qty} {row.unit}</td>
                    <td>{row.requestedBy}</td>
                    <td style={{ display: 'flex', gap: '4px' }}>
                      {row.reversedByTransferId && (
                        <Badge color="warning" appearance="filled" size="small">{t.inventory.transfersBadgeReversed}</Badge>
                      )}
                      {row.reversesTransferId && (
                        <Badge color="informative" appearance="filled" size="small">{t.inventory.transfersBadgeReverse}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {query.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
              <Button appearance="subtle" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                {t.inventory.transfersLoadMore}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
